import "./style.css";
import {
  buildResult,
  generateResultCode,
  nextQuestion,
  resolveQuestion,
  visibleQuestionIds,
  type Answers,
  type Definition,
} from "@ax-shindan/core";
import { loadDefinition } from "./loadDefinition.js";

type Screen = "loading" | "intro" | "question" | "result" | "form" | "thanks";

interface State {
  screen: Screen;
  def: Definition | null;
  questionSetId: string;
  source: "local" | "firestore";
  answers: Answers;
  currentId: string | null;
  /** この設問での暫定選択（確定はNextで） */
  draftSelection: string[];
  resultCode: string | null;
  preview: boolean;
  loadingMessage: string;
  /** 進行バーの表示%（分岐で総数が変わっても後退しないよう単調増加で保持） */
  progressPct: number;
}

const app = document.getElementById("app")!;
const state: State = {
  screen: "loading",
  def: null,
  questionSetId: "",
  source: "local",
  answers: {},
  currentId: null,
  draftSelection: [],
  resultCode: null,
  preview: false,
  loadingMessage: "読み込み中…",
  progressPct: 0,
};

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );
}

/** 空行区切りの本文を、段落<p>の連なりに変換する */
function paragraphsHtml(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${esc(p)}</p>`)
    .join("");
}

// ---------- ライフサイクル ----------
async function boot() {
  const params = new URLSearchParams(location.search);
  const previewSetId = params.get("preview") ?? undefined;
  state.preview = !!previewSetId;
  render();
  const loaded = await loadDefinition(previewSetId);
  state.def = loaded.definition;
  state.questionSetId = loaded.questionSetId;
  state.source = loaded.source;
  state.screen = "intro";
  render();
}

// ---------- 遷移 ----------
function startDiagnosis() {
  const def = state.def!;
  state.answers = {};
  state.currentId = def.startQuestion;
  state.draftSelection = [];
  state.progressPct = 0;
  state.screen = "question";
  render();
}

function selectOption(optId: string, multi: boolean, maxSelect?: number) {
  if (!multi) {
    state.draftSelection = [optId];
  } else {
    const set = new Set(state.draftSelection);
    if (set.has(optId)) set.delete(optId);
    else {
      if (maxSelect && set.size >= maxSelect) return; // 上限に達したら無視
      set.add(optId);
    }
    state.draftSelection = [...set];
  }
  render();
}

function goNext() {
  const def = state.def!;
  const id = state.currentId!;
  state.answers[id] = [...state.draftSelection];
  const next = nextQuestion(def, state.answers);
  if (next === null) {
    finishDiagnosis();
    return;
  }
  state.currentId = next;
  state.draftSelection = state.answers[next] ?? [];
  render();
  app.scrollTo?.({ top: 0 });
  window.scrollTo({ top: 0 });
}

function goBack() {
  const def = state.def!;
  const plan = visibleQuestionIds(def, state.answers);
  const idx = plan.indexOf(state.currentId!);
  if (idx <= 0) {
    state.screen = "intro";
    render();
    return;
  }
  const prev = plan[idx - 1]!;
  state.currentId = prev;
  state.draftSelection = state.answers[prev] ?? [];
  render();
}

async function finishDiagnosis() {
  const def = state.def!;
  // 結果作成中はローディング表示（サーバ側スコア計算・結果コード発行に少し時間がかかる）
  const started = Date.now();
  state.screen = "loading";
  state.loadingMessage = "診断結果を作成しています…";
  render();
  window.scrollTo({ top: 0 });

  // 確定スコアはサーバ側計算が原則（§3.2）。P1ローカル／プレビューはクライアント発行。
  if (state.source === "firestore" && !state.preview) {
    try {
      const { submitDiagnosis } = await import("./firestore.js");
      const res = await submitDiagnosis({
        questionSetId: state.questionSetId,
        answers: state.answers,
      });
      state.resultCode = res.resultCode;
    } catch (e) {
      console.warn("submitDiagnosis失敗、ローカルコードを発行", e);
      state.resultCode = generateResultCode();
    }
  } else {
    state.resultCode = generateResultCode();
  }
  void buildResult(def, state.answers); // 事前計算（例外検出）

  // スピナーが一瞬で消えてちらつかないよう、最低表示時間を確保
  const elapsed = Date.now() - started;
  if (elapsed < 900) await new Promise((r) => setTimeout(r, 900 - elapsed));

  state.screen = "result";
  render();
  window.scrollTo({ top: 0 });
}

// ---------- 描画 ----------
function render() {
  switch (state.screen) {
    case "loading":
      app.innerHTML = `
        <div class="shell">
          <div class="loading-card">
            <div class="spinner" role="status" aria-live="polite" aria-label="読み込み中"></div>
            <p class="loading-text">${esc(state.loadingMessage)}</p>
          </div>
        </div>`;
      break;
    case "intro":
      renderIntro();
      break;
    case "question":
      renderQuestion();
      break;
    case "result":
      renderResult();
      break;
    case "form":
      renderForm();
      break;
    case "thanks":
      renderThanks();
      break;
  }
  bindEvents();
}

function renderIntro() {
  const t = state.def!.uiTexts;
  app.innerHTML = `
    <div class="shell">
      ${state.preview ? `<div class="preview-banner">プレビュー中（下書きセット：${esc(state.questionSetId)}）— 回答は送信されません</div>` : ""}
      <header class="hero">
        <p class="brand">株式会社ジュリエット</p>
        <h1>${esc(t.title ?? "AI業務改善 詳細セルフ診断")}</h1>
        <p class="lead">${esc(t.intro ?? "")}</p>
        <div class="badges">
          <span class="badge">🔒 登録不要</span>
          <span class="badge">🕵️ 完全匿名</span>
          <span class="badge">⏱ ${esc(t.estimate ?? "所要7〜10分")}</span>
        </div>
        <button class="btn btn-primary" data-action="start">${esc(t.startButton ?? "診断を始める")}</button>
        <p class="reminder">${esc(t.anonymousReminder ?? "")}</p>
      </header>
    </div>`;
}

function renderQuestion() {
  const def = state.def!;
  const id = state.currentId!;
  const resolved = resolveQuestion(def, id);
  if (!resolved) {
    app.innerHTML = `<div class="card"><p>設問が見つかりません（${esc(id)}）</p></div>`;
    return;
  }

  const plan = visibleQuestionIds(def, state.answers);
  const idx = plan.indexOf(id);
  const total = plan.length;
  // 分岐で総数が変わるため、前半(基本情報Q1〜Q5)と後半(領域別設問)で重み付けし、
  // かつ単調増加にしてバーが後退しないようにする。分母の数字は表示しない。
  const fixedCount = def.fixedOrder.length;
  const PHASE1 = 0.35;
  let raw: number;
  if (idx < fixedCount) {
    raw = (idx / fixedCount) * PHASE1;
  } else {
    const p2Total = Math.max(1, total - fixedCount);
    raw = PHASE1 + ((idx - fixedCount) / p2Total) * (1 - PHASE1);
  }
  state.progressPct = Math.max(state.progressPct, Math.round(raw * 100));
  const pct = state.progressPct;

  let text = "";
  let help = "";
  let multi = false;
  let maxSelect: number | undefined;
  let minSelect = 1;
  let options: Array<{ id: string; label: string }> = [];

  if (resolved.kind === "area") {
    const a = resolved.area;
    text = a.text;
    help = a.help ?? "";
    multi = true;
    maxSelect = a.maxSelect;
    minSelect = a.minSelect;
    options = a.options.map((o) => ({ id: o.id, label: o.label }));
  } else {
    const q = resolved.question;
    text = q.text;
    multi = q.type === "multi";
    maxSelect = q.maxSelect;
    minSelect = q.minSelect ?? (multi ? 1 : 1);
    options = q.options.map((o) => ({ id: o.id, label: o.label }));
  }

  const selected = new Set(state.draftSelection);
  const valid =
    state.draftSelection.length >= minSelect &&
    (!maxSelect || state.draftSelection.length <= maxSelect);

  const optionsHtml = options
    .map((o) => {
      const on = selected.has(o.id);
      return `
      <button class="option ${on ? "selected" : ""}" data-action="opt" data-opt="${esc(o.id)}"
              role="${multi ? "checkbox" : "radio"}" aria-checked="${on}">
        <span class="mark ${multi ? "check" : "radio"}"></span>
        <span class="option-label">${esc(o.label)}</span>
      </button>`;
    })
    .join("");

  const hint = multi
    ? maxSelect
      ? `複数選択できます（最大${maxSelect}つ）`
      : "当てはまるものをすべて選んでください"
    : "1つ選んでください";

  app.innerHTML = `
    <div class="shell">
      ${state.preview ? `<div class="preview-banner">プレビュー中 — 回答は送信されません</div>` : ""}
      <div class="progress"><div class="progress-bar" style="width:${pct}%"></div></div>
      <p class="progress-label">${idx + 1}問目</p>
      <div class="card">
        <h2 class="q-text">${esc(text)}</h2>
        ${help ? `<p class="q-help">${esc(help)}</p>` : ""}
        <p class="q-hint">${esc(hint)}</p>
        <div class="options">${optionsHtml}</div>
      </div>
      <div class="actions">
        <button class="btn btn-ghost" data-action="back">${esc(def.uiTexts.backButton ?? "戻る")}</button>
        <button class="btn btn-primary" data-action="next" ${valid ? "" : "disabled"}>
          ${idx + 1 >= total ? esc(def.uiTexts.resultButton ?? "結果を見る") : esc(def.uiTexts.nextButton ?? "次へ")}
        </button>
      </div>
    </div>`;
}

function renderResult() {
  const def = state.def!;
  const { view } = buildResult(def, state.answers);
  const t = def.uiTexts;

  const themesHtml = view.themes
    .map((th, i) => {
      const tpl = th.template;
      return `
      <div class="theme">
        <div class="theme-rank">${i + 1}</div>
        <div class="theme-body">
          <h3>${esc(tpl?.headline ?? th.label)}</h3>
          ${paragraphsHtml(tpl?.body ?? "")}
          ${tpl?.change ? `<p class="theme-change">▶ ${esc(tpl.change)}</p>` : ""}
        </div>
      </div>`;
    })
    .join("");

  const cta = ctaFor(def, view.routing.primary);

  app.innerHTML = `
    <div class="shell result">
      ${state.preview ? `<div class="preview-banner">プレビュー結果 — 送信していません</div>` : ""}
      <section class="type-card">
        <p class="type-eyebrow">あなたの会社の改善タイプ</p>
        <h1 class="type-name">${esc(view.typeName)}</h1>
        <p class="type-summary">${esc(view.typeSummary)}</p>
      </section>

      <section>
        <h2 class="section-title">優先して検討したい改善テーマ 上位3件</h2>
        ${themesHtml || `<p class="muted">明確な優先テーマは検出されませんでした。まずは「AI活用の土台づくり」から始めるのがおすすめです。</p>`}
      </section>

      ${
        view.zokujinkaWarning
          ? `<section class="warn"><h2>⚠ 属人化リスクの注意</h2><p>${esc(t.zokujinkaWarning ?? "")}</p></section>`
          : ""
      }

      <section class="next-step">
        <h2 class="section-title">次のステップ</h2>
        ${view.routing.note ? `<p class="route-note">${esc(view.routing.note)}</p>` : ""}
        ${view.routing.staffAddon ? `<p class="staff-addon">📄 ${esc(view.routing.staffAddon)}</p>` : ""}
        <a class="btn btn-primary" href="#" data-action="cta">${esc(cta)}</a>
      </section>

      <section class="code-card">
        <p class="code-label">結果コード</p>
        <p class="code-value">${esc(state.resultCode ?? "—")}</p>
        <p class="code-note">お問い合わせ時にこのコードをお伝えいただくと、回答内容を踏まえた対応ができます（任意）。個人情報とは紐づきません。</p>
      </section>

      <p class="reminder">${esc(t.resultReminder ?? t.anonymousReminder ?? "")}</p>

      <div class="actions no-print">
        <button class="btn btn-ghost" data-action="print">結果をPDF保存 / 印刷</button>
        <button class="btn btn-ghost" data-action="restart">最初からやり直す</button>
      </div>

      <section class="print-footer">
        <p class="print-guide">この診断結果は、株式会社ジュリエットによる診断結果です。診断結果を受けて、自社の課題解決の相談をしたい場合には、株式会社ジュリエットまでご相談ください。</p>
        <p class="print-url">https://ax-shindan.juliet-inc.co.jp/</p>
      </section>

      <p class="footer-meta">株式会社ジュリエット｜診断日: ${new Date().toLocaleDateString("ja-JP")}</p>
    </div>`;
}

function ctaFor(def: Definition, primary: string): string {
  const t = def.uiTexts;
  switch (primary) {
    case "consult":
      return t.resultCtaConsult ?? "初回相談を申し込む";
    case "contact":
      return t.resultCtaContact ?? "お問い合わせ";
    case "shindan_strong":
    case "shindan_standard":
    default:
      return t.resultCtaShindan ?? "スタート診断について詳しく見る";
  }
}

// ---------- 初回相談フォーム ----------
// WordPress（/soudan/）の相談フォームと項目・選択肢を統一する
const EMP_OPTIONS = ["〜5名", "6〜10名", "11〜30名", "31〜50名", "51名以上"];
const Q2_TO_EMP: Record<string, string> = {
  emp_le5: "〜5名",
  emp_6_10: "6〜10名",
  emp_11_30: "11〜30名",
  emp_31_50: "31〜50名",
  emp_51plus: "51名以上",
};

/** 診断のQ2回答を、相談フォームの従業員規模の値に変換（プレフィル用） */
function employeeValue(): string {
  const id = (state.answers["Q2"] ?? [])[0];
  return id ? Q2_TO_EMP[id] ?? "" : "";
}

function renderForm() {
  const def = state.def!;
  const emp = employeeValue();
  const code = state.resultCode ?? "";
  const req = `<em class="req">必須</em>`;
  const opt = `<em class="opt">任意</em>`;

  const empOptions = EMP_OPTIONS.map(
    (e) => `<option value="${esc(e)}" ${e === emp ? "selected" : ""}>${esc(e)}</option>`,
  ).join("");

  app.innerHTML = `
    <div class="shell">
      <button class="btn btn-ghost back-link" data-action="backToResult">← 結果に戻る</button>
      <div class="card">
        <h1 class="form-title">無料オンライン相談を申し込む</h1>
        <p class="form-lead">診断結果を踏まえて、45分のオンライン相談でご相談いただけます。その場での契約やお見積りのご提示は行いません。</p>
        <form id="inquiry-form" autocomplete="on" novalidate>
          <div class="field-row">
            <label class="field"><span>会社名 ${req}</span><input type="text" name="company" required /></label>
            <label class="field"><span>お名前 ${req}</span><input type="text" name="name" required /></label>
          </div>
          <div class="field-row">
            <label class="field"><span>メールアドレス ${req}</span><input type="email" name="email" inputmode="email" required /></label>
            <label class="field"><span>電話番号 ${opt}</span><input type="tel" name="phone" inputmode="tel" /></label>
          </div>
          <label class="field">
            <span>従業員規模 ${req}</span>
            <select name="employees" required><option value="">選択してください</option>${empOptions}</select>
          </label>
          <label class="field">
            <span>結果コード ${opt}</span>
            <input type="text" name="resultCode" value="${esc(code)}" placeholder="AX-XXXX-XX" />
            <small class="field-note">診断結果のコードです。ご記入いただくと、回答内容を踏まえてお話しできます。個人情報とは紐づきません。</small>
          </label>
          <label class="field">
            <span>補足（1〜2行で結構です） ${opt}</span>
            <textarea name="note" rows="3"></textarea>
          </label>
          <fieldset class="field">
            <legend>ご希望日時 ${opt}</legend>
            <small class="field-note">ご記入いただくと日程調整がスムーズです（第1〜第4希望まで、書ける範囲で結構です）。</small>
            <div class="date-grid">
              <input type="text" name="date1" placeholder="第1希望　例：7月20日（月）14時以降" />
              <input type="text" name="date2" placeholder="第2希望　例：7月22日（水）午前" />
              <input type="text" name="date3" placeholder="第3希望" />
              <input type="text" name="date4" placeholder="第4希望" />
            </div>
          </fieldset>
          <p id="form-error" class="form-error" role="alert"></p>
          <button type="submit" class="btn btn-primary" data-action="submitInquiryForm">この内容で申し込む</button>
        </form>
        <p class="reminder">お預かりした情報は、この相談のご連絡にのみ使用します。営業メールの配信は行いません。</p>
      </div>
    </div>`;
}

function renderThanks() {
  app.innerHTML = `
    <div class="shell">
      <div class="card thanks">
        <div class="thanks-icon">✓</div>
        <h1>送信しました</h1>
        <p>お問い合わせありがとうございます。担当者より折り返しご連絡いたします。</p>
        <div class="actions">
          <button class="btn btn-primary" data-action="viewResult">診断結果に戻る</button>
          <button class="btn btn-ghost" data-action="restart">最初の画面に戻る</button>
        </div>
      </div>
    </div>`;
}

async function submitInquiryForm() {
  const form = document.getElementById("inquiry-form") as HTMLFormElement | null;
  const errEl = document.getElementById("form-error");
  const btn = app.querySelector<HTMLButtonElement>('[data-action="submitInquiryForm"]');
  if (!form) return;
  const fd = new FormData(form);
  const get = (k: string) => String(fd.get(k) ?? "").trim();

  const payload = {
    company: get("company"),
    name: get("name"),
    email: get("email"),
    phone: get("phone"),
    employees: get("employees"),
    resultCode: get("resultCode"),
    note: get("note"),
    dates: [get("date1"), get("date2"), get("date3"), get("date4")],
    ref: "shindan",
  };

  const showErr = (m: string) => { if (errEl) errEl.textContent = m; };
  showErr("");
  if (!payload.company) return showErr("会社名を入力してください。");
  if (!payload.name) return showErr("お名前を入力してください。");
  if (!payload.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email)) return showErr("メールアドレスをご確認ください。");
  if (!payload.employees) return showErr("従業員規模を選択してください。");

  if (btn) { btn.disabled = true; btn.textContent = "送信中…"; }
  try {
    if (state.source === "firestore" && !state.preview) {
      const { submitInquiry } = await import("./firestore.js");
      await submitInquiry(payload);
    } else {
      await new Promise((r) => setTimeout(r, 400)); // ローカル/プレビューはデモ
    }
    state.screen = "thanks";
    render();
    window.scrollTo({ top: 0 });
  } catch (e: any) {
    if (btn) { btn.disabled = false; btn.textContent = "送信する"; }
    showErr(`送信に失敗しました。時間をおいて再度お試しください。（${e?.message ?? e}）`);
  }
}

// ---------- イベント ----------
function bindEvents() {
  app.querySelectorAll<HTMLElement>("[data-action]").forEach((el) => {
    el.onclick = (ev) => {
      const action = el.dataset.action!;
      switch (action) {
        case "start":
          startDiagnosis();
          break;
        case "opt": {
          ev.preventDefault();
          const def = state.def!;
          const resolved = resolveQuestion(def, state.currentId!)!;
          const multi = resolved.kind === "area" || (resolved.kind === "question" && resolved.question.type === "multi");
          const maxSelect = resolved.kind === "area" ? resolved.area.maxSelect : resolved.question?.maxSelect;
          selectOption(el.dataset.opt!, multi, maxSelect);
          break;
        }
        case "next":
          goNext();
          break;
        case "back":
          goBack();
          break;
        case "print":
          window.print();
          break;
        case "restart":
          state.screen = "intro";
          state.answers = {};
          state.resultCode = null;
          render();
          break;
        case "cta":
          ev.preventDefault();
          state.screen = "form";
          render();
          window.scrollTo({ top: 0 });
          break;
        case "backToResult":
        case "viewResult":
          ev.preventDefault();
          state.screen = "result";
          render();
          window.scrollTo({ top: 0 });
          break;
        case "submitInquiryForm":
          ev.preventDefault();
          void submitInquiryForm();
          break;
      }
    };
  });
}

boot();
